// =============================================================================
// HeroLeadCard — the dramatized "back-page lead" at the top of a match page.
// Dramatization Engine, Phase 3 (docs/DRAMATIZATION-ENGINE.md).
//
// Shows the single most spectacular mined gem, dramatized (headline + body),
// with its THEMED odds as pills (the payoff). Numbers count up; the card
// reveals on mount (robust — no IntersectionObserver dependency, so it can
// never stay invisible). Self-renders nothing when there's no lead story.
//
// PASS 2 OF THE COMPONENT RECIPE: every value in this file used to be an
// inline style with a hex fallback — `#2A46D4`, `#fff`, `rgba(20,123,69,.1)`,
// `borderRadius: 3`. A component may name a token; it may never name a value
// (COMPONENT-RECIPE.md §2 rule 1), and the hexes here were a competitor's
// blue baked into the platform's most prominent card. The structure is
// unchanged; the appearance now comes entirely from the brand.
//
// The kicker + subject read as a CHIP AND A SENTENCE — the recipe's
// enrichment row — rather than as a dot and a shouty caps line.
// =============================================================================

import { useEffect, useState, type ReactElement } from 'react';
import { useCountUp } from '../../hooks/useCountUp.js';
import type { LeadStory } from '../../hooks/useLeadStory.js';
import { Chip } from '../primitives/Chip.js';
import styles from './HeroLeadCard.module.css';

function OddsPill({
  label,
  price,
  delay,
}: {
  label: string;
  price: number;
  delay: number;
}): ReactElement {
  const display = useCountUp({
    end: price,
    duration: 700,
    delay,
    decimals: 2,
    enabled: true,
  });
  return (
    <span className={styles.pill}>
      <span className={styles.pillLabel}>{label}</span>
      <strong className={styles.pillPrice}>{display}</strong>
    </span>
  );
}

export function HeroLeadCard({ story }: { story: LeadStory }): ReactElement {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const r = requestAnimationFrame(() => setVisible(true));
    return (): void => cancelAnimationFrame(r);
  }, []);

  return (
    <div className={`${styles.card} ${visible ? styles.visible : ''}`}>
      <div className={styles.head}>
        <span className={styles.subject}>
          <span className={styles.dot} aria-hidden="true" />
          {story.subject || 'Insight'}
        </span>

        {/* Dramatization Engine P4 — archetype badge. Subtle, on-brand,
            and a no-op when absent (older cached lead-story payloads). */}
        {story.archetype?.label ? (
          <Chip tone="brand" size="sm" shape="pill" variant="outline">
            {story.archetype.label}
          </Chip>
        ) : null}
      </div>

      <h2 className={styles.headline}>{story.headline}</h2>
      <p className={styles.body}>{story.body}</p>

      <div className={styles.odds}>
        {story.odds.slice(0, 3).map((o, i) => (
          <OddsPill key={o.label} label={o.label} price={o.price} delay={360 + i * 120} />
        ))}
        {story.marketLabel ? (
          <span className={styles.market}>{story.marketLabel}</span>
        ) : null}
      </div>
    </div>
  );
}

export default HeroLeadCard;
